/**
 * ROI Calculator v2 - Production Hardened
 * Works with the provided Liquid block
 * All recommendations applied:
 * - Defensive null checks everywhere
 * - Fixed loss warning (no longer requires missing #ai-roi-analysis- ID)
 * - Updated QR code CDN to reliable jsDelivr
 * - Scoped queries to prevent cross-instance bugs
 * - Early warning element creation
 * - Improved auto-selection logic
 */

class ROICalculator {
  constructor(id, defaultProductId, primaryDomain) {
    this.id = id;
    this.defaultProductId = defaultProductId;
    this.primaryDomain = primaryDomain;
    this.selectedMachine = null;

    this.elements = {};
    this.cacheElements();
    this.ensureLossWarning();
    this.init();
  }

  cacheElements() {
    const getEl = (elId) => document.getElementById(elId + this.id);

    this.elements = {
      machineInfo: getEl('machineInfo'),
      modal: getEl('modal'),
      productList: getEl('productList'),
      search: getEl('search'),
      inPresses: getEl('inPresses'),
      slPresses: getEl('slPresses'),
      inItems: getEl('inItems'),
      slItems: getEl('slItems'),
      inPrice: getEl('inPrice'),
      slPrice: getEl('slPrice'),
      inCogs: getEl('inCogs'),
      slCogs: getEl('slCogs'),
      inLabor: getEl('inLabor'),
      slLabor: getEl('slLabor'),
      inHours: getEl('inHours'),
      slHours: getEl('slHours'),
      resRevenue: getEl('resRevenue'),
      resCosts: getEl('resCosts'),
      resProfit: getEl('resProfit'),
      resMargin: getEl('resMargin'),
      resHoursProduce: getEl('resHoursProduce'),
      resDaysProduce: getEl('resDaysProduce'),
      resDailyProfit: getEl('resDailyProfit'),
      resROI: getEl('resROI'),
      analysisContainer: document.getElementById('ai-roi-analysis-' + this.id) ||
                         document.querySelector(`.ai-roi-analysis-${this.id}`)
    };
  }

  formatNumber(num) {
    if (num === null || num === undefined || isNaN(num)) return '0.00';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  formatCurrency(num) {
    return '$' + this.formatNumber(num);
  }

  calculateFinancials(params) {
    const {
      presses = 1, items = 0, price = 0, cogs = 0,
      labor = 0, hoursPerDay = 8, machinePrice = 0, capacityPerHour = 0
    } = params;

    const safePresses = Math.max(1, presses);
    const totalMachineCost = machinePrice * safePresses;
    const totalCapacityPerHour = capacityPerHour * safePresses;
    const revenue = items * price;
    const totalCogs = items * cogs;
    const hoursNeeded = totalCapacityPerHour > 0 ? items / totalCapacityPerHour : 0;
    const laborCost = hoursNeeded * labor;
    const totalCosts = totalCogs + laborCost + totalMachineCost;
    const profit = revenue - totalCosts;
    const margin = revenue > 0 ? (profit / revenue * 100) : 0;
    const daysNeeded = hoursPerDay > 0 ? hoursNeeded / hoursPerDay : 0;
    const dailyProfit = (daysNeeded > 0 && profit >= 0) ? profit / daysNeeded : 0;
    const daysToROI = dailyProfit > 0 ? totalMachineCost / dailyProfit : 0;

    return {
      revenue, totalCosts, profit, margin,
      hoursNeeded, daysNeeded, dailyProfit, daysToROI,
      totalMachineCost, totalCapacityPerHour, totalCogs, laborCost
    };
  }

  selectMachine(element) {
    if (!element || !element.dataset) return;

    const capacity = parseFloat(element.dataset.capacity);
    if (isNaN(capacity) || capacity <= 0) {
      console.warn('[ROI] Product missing valid capacity metafield');
    }

    this.selectedMachine = {
      id: parseInt(element.dataset.productId) || 0,
      handle: element.dataset.handle || '',
      title: element.dataset.title || 'Unknown Machine',
      price: parseFloat(element.dataset.price) / 100 || 0,
      capacity: isNaN(capacity) ? 0 : capacity,
      image: element.dataset.image || 'https://via.placeholder.com/300x200?text=No+Image'
    };

    const machineInfo = document.getElementById('machineInfo' + this.id);
    if (machineInfo) {
      machineInfo.innerHTML = `
        <img src="${this.selectedMachine.image}" class="ai-roi-machine-thumb-${this.id}" alt="${this.selectedMachine.title}">
        <div class="ai-roi-machine-details-${this.id}">
          <strong>${this.selectedMachine.title}</strong><br>
          Price: ${this.formatCurrency(this.selectedMachine.price)}<br>
          Output: ${this.formatNumber(this.selectedMachine.capacity)} items/hour
        </div>
        <a href="${this.primaryDomain}/products/${this.selectedMachine.handle}" target="_blank" rel="noopener noreferrer" class="ai-roi-view-product-btn-${this.id}">View Product Page</a>
      `;
      machineInfo.classList.add('active');
    }

    this.closeModal();
    this.runCalculation();
  }

  ensureLossWarning() {
    let container = this.elements.analysisContainer;

    if (!container) {
      container = document.querySelector(`.ai-roi-analysis-${this.id}`);
      if (container) this.elements.analysisContainer = container;
      else return;
    }

    let warningEl = document.getElementById('lossWarning' + this.id);
    if (!warningEl) {
      warningEl = document.createElement('div');
      warningEl.id = 'lossWarning' + this.id;
      warningEl.style.cssText = 'display:none;background:#fff3cd;padding:12px 16px;margin:0 0 16px 0;border:1px solid #ffc107;border-radius:6px;color:#856404;font-size:13px;line-height:1.4;';
      container.insertAdjacentElement('afterbegin', warningEl);
    }
  }

  filterProducts(query) {
    const term = (query || '').toLowerCase().trim();
    document.querySelectorAll(`.ai-roi-product-item-${this.id}`).forEach(item => {
      const title = (item.dataset.title || '').toLowerCase();
      item.classList.toggle('hidden', !title.includes(term));
    });
  }

  updateInput(field, value) {
    const cap = field.charAt(0).toUpperCase() + field.slice(1);
    const inp = this.elements[`in${cap}`];
    const sl = this.elements[`sl${cap}`];
    if (inp) inp.value = value;
    if (sl) sl.value = value;
    this.runCalculation();
  }

  syncSlider(field, value) {
    const cap = field.charAt(0).toUpperCase() + field.slice(1);
    const sl = this.elements[`sl${cap}`];
    const inp = this.elements[`in${cap}`];
    if (sl) sl.value = value;
    if (inp) inp.value = value;
    this.runCalculation();
  }

  runCalculation() {
    const required = ['slPresses', 'slItems', 'slPrice', 'slCogs', 'slLabor', 'slHours'];
    for (const k of required) {
      if (!this.elements[k]) {
        console.warn(`[ROI] Missing element: ${k}${this.id}`);
        return;
      }
    }

    const machine = this.selectedMachine;
    const params = {
      presses: parseFloat(this.elements.slPresses.value) || 1,
      items: parseFloat(this.elements.slItems.value) || 0,
      price: parseFloat(this.elements.slPrice.value) || 0,
      cogs: parseFloat(this.elements.slCogs.value) || 0,
      labor: parseFloat(this.elements.slLabor.value) || 0,
      hoursPerDay: parseFloat(this.elements.slHours.value) || 8,
      machinePrice: machine ? machine.price : 0,
      capacityPerHour: machine ? machine.capacity : 0
    };

    const results = this.calculateFinancials(params);
    const show = !!machine;

    this.updateDisplay(
      results.revenue, results.totalCosts, results.profit, results.margin,
      show ? results.hoursNeeded : 0,
      show ? results.daysNeeded : 0,
      show ? results.dailyProfit : 0,
      show ? results.daysToROI : 0,
      show
    );
  }

  updateDisplay(revenue, costs, profit, margin, hoursNeeded, daysNeeded, dailyProfit, roiDays, machineSelected) {
    const isLoss = profit < 0;
    const {
      resRevenue, resCosts, resProfit, resMargin,
      resHoursProduce, resDaysProduce, resDailyProfit, resROI, slHours
    } = this.elements;

    if (!resRevenue || !resCosts || !resProfit) return;

    resRevenue.textContent = this.formatCurrency(revenue);
    resCosts.textContent = this.formatCurrency(costs);
    resProfit.textContent = this.formatCurrency(profit);

    if (resProfit.style) {
      resProfit.style.color = isLoss ? '#dc3545' :
        (margin >= 50 ? '#28a745' : margin >= 25 ? '#28a745' : margin >= 10 ? '#ffc107' : '#007bff');
    }

    if (resMargin) resMargin.textContent = this.formatNumber(margin) + '%';
    if (resHoursProduce) resHoursProduce.textContent = machineSelected ? this.formatNumber(hoursNeeded) + ' hrs' : 'N/A';
    if (resDaysProduce) resDaysProduce.textContent = machineSelected ? this.formatNumber(daysNeeded) + ' days' : 'N/A';
    if (resDailyProfit) resDailyProfit.textContent = machineSelected ? this.formatCurrency(dailyProfit) : 'N/A';

    let roiText = 'N/A';
    if (machineSelected && roiDays > 0) {
      const hrs = slHours ? parseFloat(slHours.value) || 8 : 8;
      if (roiDays < 1) roiText = this.formatNumber(roiDays * hrs) + ' Hours';
      else if (roiDays <= 60) roiText = this.formatNumber(roiDays) + ' Days';
      else roiText = this.formatNumber(roiDays / 30) + ' Months';
    } else if (isLoss && machineSelected) {
      roiText = 'N/A (Loss-making)';
    }
    if (resROI) resROI.textContent = roiText;

    const warning = document.getElementById('lossWarning' + this.id);
    if (warning) {
      warning.style.display = (isLoss && machineSelected) ? 'block' : 'none';
      if (isLoss && machineSelected) {
        warning.innerHTML = `⚠️ <strong>Loss Warning:</strong> You are losing ${this.formatCurrency(Math.abs(profit))}. Consider raising prices or reducing costs.`;
      }
    }
  }

  openModal() { if (this.elements.modal) this.elements.modal.classList.add('active'); }
  closeModal() { if (this.elements.modal) this.elements.modal.classList.remove('active'); }

  downloadReport() {
    if (!this.selectedMachine) { alert('Please select a machine first'); return; }

    const getVal = (key, fb = 0) => this.elements[key] ? parseFloat(this.elements[key].value) || fb : fb;

    const params = {
      presses: getVal('slPresses', 1),
      items: getVal('slItems', 0),
      price: getVal('slPrice', 0),
      cogs: getVal('slCogs', 0),
      labor: getVal('slLabor', 0),
      hoursPerDay: getVal('slHours', 8),
      machinePrice: this.selectedMachine.price,
      capacityPerHour: this.selectedMachine.capacity
    };

    const results = this.calculateFinancials(params);
    const productUrl = `${this.primaryDomain}/products/${this.selectedMachine.handle}`;

    const wrapper = document.getElementById('roiCalcWrapper' + this.id);
    const primary = (wrapper && wrapper.dataset.primaryColor) || '#4D71F9';
    const success = (wrapper && wrapper.dataset.successColor) || '#22A200';

    let roiText = 'N/A';
    if (results.dailyProfit > 0 && results.daysToROI > 0 && results.daysToROI < 1) {
      roiText = this.formatNumber(results.daysToROI * params.hoursPerDay) + ' Hours';
    } else if (results.dailyProfit > 0) {
      roiText = this.formatNumber(results.daysToROI) + ' Days';
    } else if (results.profit < 0) {
      roiText = 'N/A (Loss-making)';
    }

    const html = `<!DOCTYPE html><html><head><title>ROI Report - ${this.selectedMachine.title}</title><meta charset="UTF-8"><style>@page{size:A4;margin:15mm}*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;padding:15px;max-width:210mm;margin:0 auto;font-size:11px;line-height:1.45;color:#222}.header{display:flex;justify-content:space-between;align-items:center;margin-bottom:15px;padding-bottom:10px;border-bottom:3px solid ${primary}}.product-image{width:120px;height:120px;object-fit:contain;border:2px solid #ddd;border-radius:6px;padding:8px;background:white}h1{color:${primary};font-size:22px;flex:1;text-align:center}h2{font-size:14px;margin:12px 0 8px;padding:6px 10px;background:#f0f0f0}.grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-bottom:12px}.item{padding:8px;background:#f9f9f9;border-left:3px solid ${primary}}.label{font-size:9px;color:#666;margin-bottom:3px}.value{font-size:13px;font-weight:bold;color:#000}.highlight{background:${success};color:white;padding:14px;text-align:center;margin:12px 0;border-radius:6px}.highlight h2{background:none;color:white;padding:0;margin:0 0 6px 0;font-size:18px}.footer{display:flex;justify-content:space-between;align-items:center;margin-top:15px;padding-top:12px;border-top:2px solid #ddd;font-size:10px}.qr-section{text-align:center}.qr-label{font-size:10px;color:#666;margin-bottom:6px;font-weight:600}#qrcode{display:inline-block;border:2px solid #ddd;padding:4px;background:white}@media print{button{display:none!important}body{padding:0}}</style></head><body><div class="header"><img src="${this.selectedMachine.image}" class="product-image" alt="${this.selectedMachine.title}"><h1>ROI Analysis Report</h1><div style="width:120px"></div></div><h2>Machine Information</h2><div class="grid"><div class="item"><div class="label">Machine</div><div class="value">${this.selectedMachine.title}</div></div><div class="item"><div class="label">Price per Machine</div><div class="value">${this.formatCurrency(this.selectedMachine.price)}</div></div><div class="item"><div class="label">Number of Presses</div><div class="value">${params.presses}</div></div><div class="item"><div class="label">Total Machine Cost</div><div class="value">${this.formatCurrency(results.totalMachineCost)}</div></div><div class="item"><div class="label">Capacity/Hour (each)</div><div class="value">${this.formatNumber(this.selectedMachine.capacity)} items</div></div><div class="item"><div class="label">Total Capacity/Hour</div><div class="value">${this.formatNumber(results.totalCapacityPerHour)} items</div></div></div><h2>Input Parameters</h2><div class="grid"><div class="item"><div class="label">Items to Produce</div><div class="value">${this.formatNumber(params.items)}</div></div><div class="item"><div class="label">Price per Item</div><div class="value">${this.formatCurrency(params.price)}</div></div><div class="item"><div class="label">Cost of Goods</div><div class="value">${this.formatCurrency(params.cogs)}</div></div><div class="item"><div class="label">Labor Cost/Hour</div><div class="value">${this.formatCurrency(params.labor)}</div></div><div class="item"><div class="label">Work Hours/Day</div><div class="value">${params.hoursPerDay}</div></div></div><h2>Financial Analysis</h2><div class="grid"><div class="item"><div class="label">Total Revenue</div><div class="value">${this.formatCurrency(results.revenue)}</div></div><div class="item"><div class="label">Total COGS</div><div class="value">${this.formatCurrency(results.totalCogs)}</div></div><div class="item"><div class="label">Labor Cost</div><div class="value">${this.formatCurrency(results.laborCost)}</div></div><div class="item"><div class="label">Total Machine Cost</div><div class="value">${this.formatCurrency(results.totalMachineCost)}</div></div><div class="item"><div class="label">Total Costs</div><div class="value">${this.formatCurrency(results.totalCosts)}</div></div><div class="item"><div class="label">Net Profit</div><div class="value">${results.profit >= 0 ? this.formatCurrency(results.profit) : '<span style="color:#dc3545">' + this.formatCurrency(results.profit) + '</span>'}</div></div><div class="item"><div class="label">Net Profit Margin</div><div class="value">${this.formatNumber(results.margin)}%</div></div><div class="item"><div class="label">Hours to Produce</div><div class="value">${this.formatNumber(results.hoursNeeded)} hrs</div></div><div class="item"><div class="label">Days to Produce</div><div class="value">${this.formatNumber(results.daysNeeded)} days</div></div><div class="item"><div class="label">Daily Profit</div><div class="value">${this.formatCurrency(results.dailyProfit)}</div></div></div><div class="highlight"><h2>Time to ROI: ${roiText}</h2><p style="font-size:15px;font-weight:600">Daily Profit After ROI: ${this.formatCurrency(results.dailyProfit)}</p></div><div class="footer"><div style="flex:1"><p style="font-size:10px;color:#666">Generated on ${new Date().toLocaleDateString()}</p><p style="font-size:10px;color:#666;margin-top:4px">Visit: www.microtecusa.com</p></div><div class="qr-section"><div class="qr-label">Scan to purchase:</div><div id="qrcode"></div></div></div><button onclick="window.print()" style="margin-top:15px;padding:10px 20px;background:${primary};color:white;border:none;border-radius:4px;cursor:pointer;font-size:14px;font-weight:600">Print / Save as PDF</button><script src="https://cdn.jsdelivr.net/npm/qrcodejs@1.0.0/qrcode.min.js"><\/script><script>window.addEventListener("load",function(){if(typeof QRCode!=="undefined")new QRCode(document.getElementById("qrcode"),{text:"${productUrl}",width:120,height:120,colorDark:"#000000",colorLight:"#ffffff",correctLevel:QRCode.CorrectLevel.H});});<\/script></body></html>`;

    const w = window.open('', '_blank', 'width=900,height=1100');
    if (!w) { alert('Popup blocked — please allow popups'); return; }
    w.document.write(html);
    w.document.close();
  }

  init() {
    // Auto-select current product on /products/ pages
    if (window.location.pathname.includes('/products/')) {
      let currentId = null;
      const container = document.querySelector(`.ai-roi-calculator-${this.id}`);
      if (container?.dataset.currentProductId && container.dataset.currentProductId !== 'null') {
        currentId = container.dataset.currentProductId;
      }
      if (!currentId && this.defaultProductId && this.defaultProductId !== 'null' && !isNaN(parseInt(this.defaultProductId))) {
        currentId = this.defaultProductId;
      }

      if (currentId) {
        const list = this.elements.productList || document.getElementById('productList' + this.id);
        const item = list ? list.querySelector(`[data-product-id="${currentId}"]`) : null;
        if (item) {
          setTimeout(() => this.selectMachine(item), 280);
          return;
        }
      }
    }

    // Fallback to default product
    if (this.defaultProductId && this.defaultProductId !== 'null' && !isNaN(parseInt(this.defaultProductId))) {
      const list = this.elements.productList || document.getElementById('productList' + this.id);
      const item = list ? list.querySelector(`[data-product-id="${this.defaultProductId}"]`) : null;
      if (item) {
        this.selectMachine(item);
        return;
      }
    }

    // Last resort: first machine in this calculator
    if (!this.selectedMachine) {
      const list = this.elements.productList || document.getElementById('productList' + this.id);
      const first = list ? list.querySelector(`.ai-roi-product-item-${this.id}`) : null;
      if (first) this.selectMachine(first);
    }

    if (!this.selectedMachine) {
      console.warn('[ROI Calculator] No machines with valid capacity found');
    }

    this.runCalculation();
  }
}

// Global entry point (called from Liquid)
window.initROICalc = function(id, defaultProductId, primaryDomain, blockDefaultId) {
  const effective = (defaultProductId && defaultProductId !== 'null') ? defaultProductId : (blockDefaultId || 'null');
  const instance = new ROICalculator(id, effective, primaryDomain);
  window['roiCalc' + id] = instance;
  if (id === '07ad21c2') window.ROIcalculator = instance;
  return instance;
};

window.ROICalculator = ROICalculator;